import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useRef,
} from "react";
import WebSocketService from "./WebSocketService";
import { GlobalContext } from "./GlobalContext";
import { jwtDecode } from "jwt-decode";

const WebSocketContext = createContext(null);

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
    const [connectionStatus, setConnectionStatus] = useState(
        WebSocketService.ws ? WebSocketService.ws.readyState : WebSocket.CONNECTING
    );
    const [statusMessage, setStatusMessage] = useState(null);
    const reconnectTimeoutRef = useRef(null);
    const hasConnectedBefore = useRef(false);
    const { auth, refreshAccessToken } = useContext(GlobalContext);
    const { accessToken } = auth;

    const scheduleReconnectMessage = useCallback(() => {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = setTimeout(() => {
            setStatusMessage("Reconnecting...");
        }, 5000);
    }, []);

    useEffect(() => {
        if (window.Capacitor && accessToken) {
            window.capacitorToken = accessToken;
        }
    }, [accessToken]);

    // Handle WebSocket open
    const handleOpen = useCallback(() => {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
        setConnectionStatus(WebSocket.OPEN);
        console.log("WebSocket opened");

        if (hasConnectedBefore.current) {
            // Only show the banner after a reconnect, not on the first successful open
            setStatusMessage("Connected");
            setTimeout(() => setStatusMessage(null), 1000);
        } else {
            // First-ever connection: keep the banner hidden
            setStatusMessage(null);
            hasConnectedBefore.current = true;
        }
    }, []);

    // Handle WebSocket close
    const handleClose = useCallback(
        (event) => {
            // If the server closes the connection due to authentication failure, refresh token.
            if (
                event.code === 1008 &&
                event.reason &&
                event.reason.includes("Authentication failed")
            ) {
                console.warn(
                    "WebSocket closed due to authentication failure. Refreshing token..."
                );
                refreshAccessToken().then((success) => {
                    if (success) {
                        console.log("Token refreshed after auth failure.");
                    } else {
                        console.error("Failed to refresh token after auth failure.");
                    }
                });
            }
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
            const wasClean = event && event.wasClean;
            setConnectionStatus(wasClean ? WebSocket.CLOSED : WebSocket.CONNECTING);
            console.log("WebSocket closed", JSON.stringify(event));
            if (!wasClean) {
                setStatusMessage("Connection lost");
            }
        },
        [refreshAccessToken]
    );

    // Handle WebSocket error
    const handleError = useCallback((error) => {
        console.error("WebSocket encountered error:", error);
    }, []);

    const handleReconnecting = useCallback(() => {
        scheduleReconnectMessage();
        setConnectionStatus(WebSocket.CONNECTING);
    }, [scheduleReconnectMessage]);

    useEffect(() => {
        if (connectionStatus === WebSocket.CONNECTING) {
            // If we’re still connecting after 5 s, show the banner
            if (!reconnectTimeoutRef.current) {
                scheduleReconnectMessage();
            }
        } else if (connectionStatus === WebSocket.OPEN) {
            // Clear the timer once we’re connected
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    }, [connectionStatus, scheduleReconnectMessage]);

    // Subscribe/unsubscribe to WebSocketService events
    useEffect(() => {
        WebSocketService.on("open", handleOpen);
        WebSocketService.on("close", handleClose);
        WebSocketService.on("error", handleError);
        WebSocketService.on("reconnecting", handleReconnecting);
        return () => {
            WebSocketService.off("open", handleOpen);
            WebSocketService.off("close", handleClose);
            WebSocketService.off("error", handleError);
            WebSocketService.off("reconnecting", handleReconnecting);
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        };
    }, [handleOpen, handleClose, handleError, handleReconnecting]);

    // Send messages via the service
    const sendMessage = useCallback((message) => {
        WebSocketService.sendMessage(message);
    }, []);

    // When the connection is open (or token updates), verify token validity and send the authentication message.
    useEffect(() => {
        if (connectionStatus === WebSocket.OPEN && accessToken) {
            let tokenValid = true;
            let decoded = null;
            try {
                decoded = jwtDecode(accessToken);
                if (decoded.exp * 1000 < Date.now()) tokenValid = false;
            } catch (error) {
                //console.error("[WS_AUTH] decode error", error);
                tokenValid = false;
            }

            if (!tokenValid) {
                //console.log("[WS_AUTH] token expired, refreshing...");
                refreshAccessToken().then((success) => {
                    if (success) {
                        //console.log("[WS_AUTH] refreshed, sending authenticate");
                        WebSocketService.sendMessage({
                            type: "authenticate",
                            token: auth.accessToken,
                        });
                    } else {
                        console.error("[WS_AUTH] refresh failed");
                    }
                });
            } else {
                /* console.log("[WS_AUTH] sending authenticate", {
                    exp: decoded?.exp ? new Date(decoded.exp * 1000).toISOString() : null,
                }); */
                WebSocketService.sendMessage({ type: "authenticate", token: accessToken });
            }
        }
    }, [connectionStatus, accessToken, refreshAccessToken, auth.accessToken]);

    const value = { connectionStatus, sendMessage, statusMessage };
    return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

export default WebSocketContext;
